import React, { useMemo, useState, useEffect, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import MainActivityPagingControls from '@monorepo/shared/src/components/modules/Paging/components/MainActivityPagingControls';
import DoublePagePdf from '@monorepo/shared/src/components/common/Pdf/DoublePagePdf';
import { getPageNumber } from '@monorepo/shared/src/components/common/Pdf/util/pdf-util';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import { StudentPracticeBookLevel, ActivityNames } from '@monorepo/shared/src/types';
import { BookType, AttemptStatus } from '@monorepo/shared/src/graph/schema-generated-types-student';
import useLetterTeaching from '@monorepo/shared/src/hooks/useLetterTeaching';
import useActivityAudio from '@monorepo/shared/src/hooks/use-activity-audio';
import usePageViewAutoSwitch from '@monorepo/shared/src/hooks/use-page-view-auto-switch';
import useAttemptStore from '@monorepo/shared/src/graph/state/query-hooks/use-attempt-store';
import config from '@monorepo/shared/src/config';
import ImageButton from 'components/common/Buttons/ImageButton';
import useUserStore from 'graph/state/query-hooks/use-user';
import useUploadPage from 'state/hooks/use-upload-page';
import { InstructionButtonContext } from 'state/providers/InstructionsButtonProvider';
import ActivitiesLayout from '../components/ActivitiesLayout';

type Props = {
  assignmentId: number;
};

const PracticeBook: React.FC<Props> = ({ assignmentId }) => {
  const { search } = useLocation();
  const { assignmentActivityId } = useParams();
  const query = new URLSearchParams(search);
  const bookCode = query.get('bookCode');
  const letter = bookCode?.slice(-1);
  const [user] = useUserStore();

  const isMainPage = false;
  const [{ books }] = usePageStore(BookType.PRACTICE_BOOK, letter);
  const [practiceBookType, setPracticeBookType] = useState(StudentPracticeBookLevel.Emerging);
  const [playedInstructions, setPlayedInstructions] = useState(false);

  const practiceBook = books?.find((b) => b.title.includes(practiceBookType));

  const showPdf = useMemo(() => !!practiceBook?.url, [practiceBook]);

  const [{ sounds, currentSound }, { playSoundFromState, loadAudioAssets, stopSound }] =
    useActivityAudio();
  const [{ playInstructionFlag }, { stopInstructions, setCurrentInstruction }] =
    useContext(InstructionButtonContext);

  const { PageList, pagesToDisplay } = useLetterTeaching(practiceBook, isMainPage);
  const [, { handleSaveAttempt }] = useAttemptStore();
  const { setCurrentStep } = useUploadPage(
    practiceBook,
    assignmentActivityId,
    ActivityNames.PRACTICE_BOOK
  );

  usePageViewAutoSwitch();

  const instructions = `${config.digitalAssetsUrl}/activities/en-us/tap-on-the-button-to-print-or-to-turn-in-your-work.mp3`;

  useEffect(() => {
    if (!user?.me?.groups?.length) return;
    if (user?.me?.groups[0].name === StudentPracticeBookLevel.Developing)
      setPracticeBookType(StudentPracticeBookLevel.Developing);
  }, [user?.me?.groups]);

  useEffect(() => {
    assignmentActivityId &&
      handleSaveAttempt({
        assignmentActivityId: JSON.parse(assignmentActivityId),
        status: AttemptStatus.IN_PROGRESS,
        dateStarted: new Date(),
      });
  }, [assignmentActivityId]);

  useEffect(() => {
    currentSound && stopSound();
    const loadAudio = async (audio) => {
      await loadAudioAssets([
        {
          name: 'instructions',
          category: 'instructions',
          src: { src: audio },
        },
      ]);
    };
    loadAudio(instructions);

    return () => {
      currentSound && stopSound();
    };
  }, []);

  useEffect(() => {
    const playInstructions = async () => {
      setPlayedInstructions(true);
      setCurrentInstruction('instructions');
      await playSoundFromState(sounds['instructions'], true);
      stopInstructions();
    };

    if (!playedInstructions && sounds['instructions']) {
      playInstructions();
    }
  }, [playedInstructions, sounds['instructions']]);

  useEffect(() => {
    const playSound = async () => {
      currentSound && stopSound();
      await playSoundFromState(sounds['instructions'], true);
      stopInstructions();
    };

    if (sounds['instructions'] && playInstructionFlag) {
      playSound();
    }
  }, [sounds['instructions'], playInstructionFlag]);

  return (
    <ActivitiesLayout
      assignmentId={assignmentId}
      footerProps={{
        children: (
          <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ImageButton
              onClick={() => {
                stopInstructions();
                setCurrentStep('havePrinter');
              }}
              style={{ margin: 15 }}
              imageName={'printButton'}
            />
            <ImageButton
              onClick={() => {
                stopInstructions();
                setCurrentStep('submitPage');
              }}
              style={{ margin: 15 }}
              imageName={'uploadButton'}
            />
          </Box>
        ),
      }}
      gutterBottom
    >
      <MainActivityPagingControls
        isMainPage={isMainPage}
        pagination={false}
        displayZoomControls
        hasFooter
        hasCover={isMainPage}
        bookLayout={Boolean(!showPdf)}
        height="100%"
      >
        {showPdf ? (
          <DoublePagePdf
            paperWrapper={!!showPdf}
            leftPage={{
              documentProps: { file: practiceBook?.url },
              pageProps: {
                pageNumber: getPageNumber('leftPage', pagesToDisplay),
              },
            }}
            rightPage={{
              documentProps: { file: practiceBook?.url },
              pageProps: {
                pageNumber: getPageNumber('rightPage', pagesToDisplay),
              },
            }}
          />
        ) : (
          PageList
        )}
      </MainActivityPagingControls>
    </ActivitiesLayout>
  );
};

export default PracticeBook;
